import type { GaApp } from '~/modules/app'
import { LANGUAGE } from '~/modules/app'
import type {
  InitialModalQueueFlags,
  ModalQueueStep,
  ModalQueueStepType,
} from '~/modules/main'

/**
 * Централизованная очередь стартовых модалок.
 * Модалки не знают «кто следующий» — только complete / clear / resumeOnHome.
 */
export class ModalQueueService {
  gaApp: GaApp

  private steps: ModalQueueStep[] = []
  private current: ModalQueueStep | null = null
  /**
   * Текущий шаг с requireHome ждёт перехода на '/'.
   * Пока true, UI шага не открыт — продолжение через resumeOnHome.
   */
  private deferred = false

  constructor(gaApp: GaApp) {
    this.gaApp = gaApp
  }

  /**
   * Единственное место, где описан порядок стартового флоу.
   */
  buildInitialSteps(flags: InitialModalQueueFlags): ModalQueueStep[] {
    const {
      showPromoModal,
      showRatingResultsModal,
      showDailyReward,
      dailyRewardCount,
    } = flags

    const needLanguageSwitcher =
      this.gaApp.i18n.locales.value.length > 1 &&
      this.gaApp.i18n.locale.value.code !== LANGUAGE.KK

    const dailyStep = (requireHome = false): ModalQueueStep => ({
      type: 'dailyReward',
      payload: { dailyRewardCount, requireHome },
    })

    if (showPromoModal) {
      return [
        { type: 'onboarding' },
        ...(needLanguageSwitcher
          ? [{ type: 'languageSwitcher' as const }]
          : []),
        { type: 'ensureHome' },
        ...(showDailyReward ? [dailyStep()] : []),
      ]
    }

    if (showRatingResultsModal) {
      return [
        { type: 'ratingResults' },
        ...(showDailyReward ? [dailyStep(true)] : []),
      ]
    }

    if (showDailyReward) {
      if (needLanguageSwitcher) {
        return [
          { type: 'languageSwitcher' },
          { type: 'ensureHome' },
          dailyStep(),
        ]
      }

      // Daily только на главной
      return [{ type: 'ensureHome' }, dailyStep()]
    }

    return []
  }

  /** Старт очереди по флагам GET /user */
  startInitial(flags: InitialModalQueueFlags): void {
    const steps = this.buildInitialSteps(flags)

    if (steps.length === 0) {
      return
    }

    this.clear()
    this.steps = steps
    void this.runNext()
  }

  /**
   * Текущий шаг завершён (finish-close модалки).
   * @param expectedType complete только когда текущий шаг совпадает
   *   (защита от повторного finish-close и ручного open вне очереди)
   */
  async complete(expectedType: ModalQueueStepType): Promise<void> {
    if (this.current?.type !== expectedType) {
      return
    }

    if (this.current == null && this.steps.length === 0) {
      return
    }

    this.current = null
    await this.runNext()
  }

  /** Сброс очереди (смена языка → reload; daily придёт снова из GET /user) */
  clear(): void {
    this.steps = []
    this.current = null
    this.deferred = false
  }

  /**
   * Переход на '/' (если нужно) и продолжение отложенного шага.
   * await вместо .then — push может не вернуть Promise.
   */
  async resumeOnHome(): Promise<void> {
    if (this.gaApp.route.path !== '/') {
      await this.gaApp.router.push('/')
    }

    if (!this.deferred || !this.current) {
      return
    }

    this.deferred = false
    await this.executeCurrent()
  }

  private async runNext(): Promise<void> {
    if (this.current) {
      return
    }

    const next = this.steps.shift()

    if (!next) {
      return
    }

    this.current = next
    await this.executeCurrent()
  }

  private async executeCurrent(): Promise<void> {
    const step = this.current

    if (!step) {
      return
    }

    switch (step.type) {
      case 'onboarding':
        await this.runOnboarding()
        break
      case 'languageSwitcher':
        this.runLanguageSwitcher()
        break
      case 'ratingResults':
        await this.runRatingResults()
        break
      case 'ensureHome':
        await this.runEnsureHome()
        break
      case 'dailyReward':
        await this.runDailyReward(step)
        break
    }
  }

  /**
   * Шаг с requireHome: если не на главной — откладываем открытие UI.
   * Подготовку данных (если нужна) вызывающий код делает до этой проверки.
   */
  private deferIfRequireHome(step: ModalQueueStep): boolean {
    if (step.payload?.requireHome !== true || this.gaApp.route.path === '/') {
      return false
    }

    this.deferred = true
    return true
  }

  private async whenAppMounted(open: () => void): Promise<void> {
    return new Promise((resolve) => {
      const run = (): void => {
        open()
        resolve()
      }

      if (this.gaApp.stores.app.main.appMounted) {
        run()
      } else {
        const unsubscribe = this.gaApp.eventBus.subscribe(
          'module/app/router/page-mounted',
          () => {
            unsubscribe?.()
            run()
          },
        )
      }
    })
  }

  private async runOnboarding(): Promise<void> {
    const onboardingModal = this.gaApp.stores.main.modals.onboarding

    if (onboardingModal.hasAutoShown || onboardingModal.isOpened) {
      await this.complete('onboarding')
      return
    }

    await this.whenAppMounted(() => {
      onboardingModal.hasAutoShown = true
      this.gaApp.services.main.modal.openOnboardingModal()
    })
  }

  private runLanguageSwitcher(): void {
    this.gaApp.services.main.user.disableGetUserTimer()
    this.gaApp.services.main.modal.openLanguageSwitcherModal()
  }

  private async runRatingResults(): Promise<void> {
    const user = this.gaApp.stores.main.user.data

    if (!user?.showRatingResultsModal) {
      await this.complete('ratingResults')
      return
    }

    const ratingResultsModal = this.gaApp.stores.main.modals.ratingResults

    if (ratingResultsModal.hasAutoShown || ratingResultsModal.isOpened) {
      await this.complete('ratingResults')
      return
    }

    await this.whenAppMounted(() => {
      ratingResultsModal.hasAutoShown = true
      this.gaApp.services.main.modal.openRatingResultsModal()
    })
  }

  private async runEnsureHome(): Promise<void> {
    if (this.gaApp.route.path !== '/') {
      await this.gaApp.router.push('/')
    }

    await this.complete('ensureHome')
  }

  private async runDailyReward(step: ModalQueueStep): Promise<void> {
    const count = step.payload?.dailyRewardCount

    if (count == null) {
      await this.complete('dailyReward')
      return
    }

    this.gaApp.services.main.main.prepareDailyReward(count)

    if (this.deferIfRequireHome(step)) {
      return
    }

    await this.openDailyRewardModal()
  }

  private async openDailyRewardModal(): Promise<void> {
    await this.whenAppMounted(() => {
      this.gaApp.services.main.modal.openDailyRewardModal()
    })
  }
}
